<template>
  <div>
    <v-row class="ma-0 my-4 align-center justify-space-between">
      <h1 class="text-h5 font-weight-bold">Administradores</h1>
      <v-btn v-if="canCreate" color="primary" rounded @click="openCreateDialog">
        <v-icon left small>mdi-plus</v-icon>Crear administrador
      </v-btn>
    </v-row>

    <v-alert v-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>

    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-row class="px-4 pt-4 pb-2">
            <v-col cols="12" sm="5" class="py-0">
              <v-select
                v-model="filterStatus"
                :items="filterStatusOptions"
                item-text="label"
                item-value="value"
                dense
                hide-details
                outlined
                label="Estado"
                @change="loadEjecutivos"
              ></v-select>
            </v-col>
          </v-row>
          <v-data-table
            :headers="headers"
            :items="ejecutivos"
            :loading="loading"
            hide-default-footer
            :items-per-page="-1"
            class="elevation-0"
            no-data-text="No hay ejecutivos registrados"
          >
            <template v-slot:item.name="{ item }">
              {{ item.name }} {{ item.surname }}
            </template>
            <template v-slot:item.rut="{ item }">
              {{ item.rut ? formatRut(item.rut) : '—' }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="item.status === 1 ? 'success' : 'error'" dark>
                {{ item.status === 1 ? 'Activo' : 'Inactivo' }}
              </v-chip>
            </template>
            <template v-slot:item.permissions="{ item }">
              <v-chip v-if="permissionSummary(item)" x-small outlined class="mr-1 my-1">
                {{ permissionSummary(item) }}
              </v-chip>
              <span v-else class="text-caption grey--text">Sin permisos</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip v-if="canChangeStatus" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    x-small
                    depressed
                    rounded
                    v-bind="attrs"
                    v-on="on"
                    :color="item.status === 1 ? 'error' : 'success'"
                    :loading="togglingId === item.id"
                    :disabled="!!togglingId"
                    @click="toggleStatus(item)"
                  >
                    {{ item.status === 1 ? 'Desactivar' : 'Activar' }}
                  </v-btn>
                </template>
                <span>{{ item.status === 1 ? 'Desactivar usuario' : 'Activar usuario' }}</span>
              </v-tooltip>
              <v-tooltip v-if="canResetMfa" top>
                <!-- The activator is the span, not the button: a disabled v-btn
                     emits no mouse events, so the tooltip explaining WHY it is
                     disabled would never appear. -->
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on" class="d-inline-block ml-2">
                    <v-btn
                      x-small
                      depressed
                      rounded
                      :color="item.mfaEnabled ? 'warning' : 'grey lighten-2'"
                      :loading="resettingId === item.id"
                      :disabled="!!resettingId || !item.mfaEnabled"
                      @click="askResetMfa(item)"
                    >
                      <v-icon x-small left>
                        {{ item.mfaEnabled ? 'mdi-shield-refresh' : 'mdi-shield-alert' }}
                      </v-icon>
                      {{ item.mfaEnabled ? 'Resetear MFA' : 'MFA pendiente' }}
                    </v-btn>
                  </span>
                </template>
                <span>
                  {{ item.mfaEnabled
                    ? 'Eliminar su segundo factor para que registre un dispositivo nuevo'
                    : 'Aún no vincula una app de autenticación. No puede operar el panel.' }}
                </span>
              </v-tooltip>
              <v-btn
                v-if="canUpdate"
                x-small
                depressed
                rounded
                color="primary"
                class="ml-2"
                @click="openEditDialog(item)"
              >
                <v-icon x-small left>mdi-pencil</v-icon>
                Editar
              </v-btn>
              <!-- An admin never edits their own tags (the backend refuses it too). -->
              <v-btn
                v-if="canManagePermissions && item.id !== currentAdminId"
                x-small
                depressed
                rounded
                color="primary"
                class="ml-2"
                @click="openPermissionsDialog(item)"
              >
                <v-icon x-small left>mdi-key-variant</v-icon>
                Permisos
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="createDialog" max-width="480" :persistent="creating">
      <v-card>
        <v-card-title class="text-h6">Crear administrador</v-card-title>
        <v-card-text>
          <v-form ref="createForm" v-model="createValid" lazy-validation @submit.prevent="submitCreate">
            <v-text-field v-model="newAdmin.name" :rules="requiredRule" label="Nombre" required></v-text-field>
            <v-text-field v-model="newAdmin.surname" :rules="requiredRule" label="Apellido" required></v-text-field>
            <v-text-field
              :value="newAdmin.rut"
              :rules="requiredRutRules"
              label="RUT"
              hint="Ej: 12.345.678-5"
              required
              @input="onRutInput(newAdmin, $event)"
            ></v-text-field>
            <v-text-field v-model="newAdmin.email" :rules="emailRules" label="Correo electrónico" required></v-text-field>
            <!-- Initial tags are a permission edit: the backend refuses them
                 without admins:manage-permissions. -->
            <v-select
              v-if="canManagePermissions"
              v-model="newAdmin.profileKey"
              :items="profileOptions"
              item-text="label"
              item-value="key"
              label="Perfil de permisos"
              hint="Sin perfil, el administrador nace sin accesos."
              persistent-hint
            ></v-select>
          </v-form>
          <v-alert v-if="createError" dense type="error" class="mb-0">{{ createError }}</v-alert>
          <p class="text-caption mb-0 mt-3">
            Recibirá su contraseña por correo. En su primer ingreso deberá registrar
            la verificación en dos pasos antes de poder operar el panel.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="creating" @click="createDialog = false">Cancelar</v-btn>
          <v-btn color="primary" text :loading="creating" @click="submitCreate">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="480" :persistent="editing">
      <v-card>
        <v-card-title class="text-h6">Editar administrador</v-card-title>
        <v-card-text>
          <v-form ref="editForm" v-model="editValid" lazy-validation @submit.prevent="submitEdit">
            <v-text-field v-model="editAdmin.name" :rules="requiredRule" label="Nombre"></v-text-field>
            <v-text-field v-model="editAdmin.surname" :rules="requiredRule" label="Apellido"></v-text-field>
            <v-text-field
              :value="editAdmin.rut"
              :rules="editRutRules"
              label="RUT"
              hint="Ej: 12.345.678-5"
              @input="onRutInput(editAdmin, $event)"
            ></v-text-field>
            <v-text-field v-model="editAdmin.email" :rules="emailRules" label="Correo electrónico"></v-text-field>
          </v-form>
          <v-alert v-if="editError" dense type="error" class="mb-0">{{ editError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="editing" @click="editDialog = false">Cancelar</v-btn>
          <v-btn color="primary" text :loading="editing" @click="submitEdit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <admin-permissions-dialog
      v-model="permissionsDialog"
      :admin="permissionsTarget"
      :catalog="catalog"
      @saved="onPermissionsSaved"
    ></admin-permissions-dialog>

    <v-snackbar v-model="snackbar" color="success" timeout="5000">{{ snackbarText }}</v-snackbar>

    <v-dialog v-model="confirmMfaReset" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Resetear verificación en dos pasos</v-card-title>
        <v-card-text>
          Se eliminará el segundo factor de <strong>{{ mfaResetTarget && mfaResetTarget.name }}</strong>
          y sus códigos de recuperación dejarán de servir. Deberá registrar un dispositivo nuevo en su
          próximo ingreso.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmMfaReset = false">Cancelar</v-btn>
          <v-btn color="warning" text @click="resetMfa">Resetear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EjecutivosService from '../services/ejecutivos.service';
import MfaService from '../services/mfa.service';
import AdminPermissionsDialog from '../components/AdminPermissionsDialog.vue';
import {
  can,
  getCurrentAdminId,
  loadPermissions,
  permissionErrorMessage
} from '../utils/permissions';
import { summarizePermissions } from '../utils/admin-permissions.utils';
import RutUtils from '../utils/rut.utils';

const ADMIN_ERROR_MESSAGES = {
  ADMIN_RUT_TAKEN: 'Ya existe un administrador con ese RUT',
  ADMIN_EMAIL_TAKEN: 'Ya existe un administrador con ese correo',
  USER_ADMIN_ALREADY_EXISTS: 'Ya existe un administrador con ese correo',
  VALIDATION_ERROR: 'Revisa los datos ingresados: el RUT o el correo no son válidos.',
  INVALID_BODY: 'Revisa los datos ingresados: nombre, apellido, RUT y correo son obligatorios.',
  USER_ADMIN_NOT_FOUND: 'El administrador ya no existe. Recarga la página.'
};

const NO_PROFILE = { key: null, label: 'Sin perfil (sin accesos)', permissions: [] };

export default {
  name: 'Ejecutivos',
  components: { AdminPermissionsDialog },
  data() {
    return {
      loading: false,
      error: null,
      togglingId: null,
      resettingId: null,
      confirmMfaReset: false,
      mfaResetTarget: null,
      createDialog: false,
      createValid: true,
      creating: false,
      createError: null,
      newAdmin: { name: '', surname: '', email: '', rut: '', profileKey: null },
      editDialog: false,
      editValid: true,
      editing: false,
      editError: null,
      editTarget: null,
      editAdmin: { name: '', surname: '', email: '', rut: '' },
      // Admin management (2026-10-30): one tag per action.
      canCreate: false,
      canChangeStatus: false,
      canUpdate: false,
      canManagePermissions: false,
      // Spec 132: resetting MFA has its own tag.
      canResetMfa: false,
      currentAdminId: null,
      catalog: null,
      permissionsDialog: false,
      permissionsTarget: null,
      requiredRule: [v => !!(v && v.trim()) || 'Campo requerido'],
      emailRules: [
        v => !!v || 'El correo electrónico es requerido',
        v => /.+@.+\..+/.test(v) || 'El correo debe ser válido'
      ],
      rutFormatRule: v => RutUtils.validateRut(v) || 'El RUT no es válido',
      snackbar: false,
      snackbarText: '',
      ejecutivos: [],
      filterStatus: 'active',
      filterStatusOptions: [
        { label: 'Solo activos', value: 'active' },
        { label: 'Todos',        value: 'all' },
      ],
      headers: [
        { text: 'Nombre',   value: 'name',    sortable: true },
        { text: 'RUT',      value: 'rut',     sortable: true },
        { text: 'Email',    value: 'email',   sortable: true },
        { text: 'Estado',   value: 'status',  sortable: false },
        { text: 'Permisos', value: 'permissions', sortable: false },
        { text: 'Acciones', value: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    requiredRutRules() {
      return [v => !!v || 'El RUT es requerido', this.rutFormatRule];
    },
    // Admins created before the RUT existed may keep it empty; once set it cannot be cleared.
    editRutRules() {
      const hadRut = !!(this.editTarget && this.editTarget.rut);
      return hadRut ? this.requiredRutRules : [this.rutFormatRule];
    },
    profileOptions() {
      return [NO_PROFILE, ...((this.catalog && this.catalog.profiles) || [])];
    }
  },
  async mounted() {
    this.loadEjecutivos();
    this.loadCatalog();
    await loadPermissions();
    this.canCreate = can('admins:create');
    this.canChangeStatus = can('admins:change-status');
    this.canUpdate = can('admins:update');
    this.canManagePermissions = can('admins:manage-permissions');
    this.canResetMfa = can('admins:reset-mfa');
    this.currentAdminId = getCurrentAdminId();
  },
  methods: {
    formatRut(rut) {
      return RutUtils.formatRut(rut);
    },
    onRutInput(target, value) {
      target.rut = RutUtils.formatRut(value);
    },
    adminErrorMessage(res, fallback) {
      return ADMIN_ERROR_MESSAGES[res.code] || permissionErrorMessage(res, fallback);
    },
    async loadCatalog() {
      const res = await EjecutivosService.getPermissionsCatalog();
      if (res.status !== 'ok') return;
      this.catalog = res.data;
    },
    // Profile name when the tags match one exactly, else "Personalizado (N)" (spec 132).
    permissionSummary(item) {
      return summarizePermissions(item.permissions, this.catalog);
    },
    openPermissionsDialog(item) {
      this.permissionsTarget = item;
      this.permissionsDialog = true;
    },
    onPermissionsSaved(data) {
      if (this.permissionsTarget && data && Array.isArray(data.permissions)) {
        this.permissionsTarget.permissions = data.permissions;
      }
      this.snackbarText = 'Permisos actualizados.';
      this.snackbar = true;
    },
    async loadEjecutivos() {
      this.loading = true;
      const all = this.filterStatus === 'all';
      const res = await EjecutivosService.getAll(all);
      if (res.status === 'ok') this.ejecutivos = res.data;
      else this.error = 'No se pudieron cargar los ejecutivos.';
      this.loading = false;
    },
    async toggleStatus(item) {
      this.togglingId = item.id;
      const res = await EjecutivosService.toggleStatus(item.id);
      if (res.status === 'ok') {
        item.status = item.status === 1 ? 0 : 1;
      } else {
        this.error = permissionErrorMessage(res, 'No se pudo cambiar el estado del usuario.');
      }
      this.togglingId = null;
    },
    openCreateDialog() {
      this.newAdmin = { name: '', surname: '', email: '', rut: '', profileKey: null };
      this.createError = null;
      this.createDialog = true;
      if (this.$refs.createForm) this.$refs.createForm.resetValidation();
    },
    async submitCreate() {
      if (!this.$refs.createForm.validate()) return;

      this.creating = true;
      this.createError = null;
      const profile = this.canManagePermissions
        ? this.profileOptions.find((p) => p.key === this.newAdmin.profileKey)
        : null;
      const res = await EjecutivosService.create({
        name: this.newAdmin.name,
        surname: this.newAdmin.surname,
        email: this.newAdmin.email,
        rut: this.newAdmin.rut,
        permissions: profile ? profile.permissions : []
      });
      if (res.status === 'ok') {
        this.createDialog = false;
        this.snackbarText = `Administrador creado. Se envió la contraseña a ${this.newAdmin.email}.`;
        this.snackbar = true;
        await this.loadEjecutivos();
      } else {
        this.createError = this.adminErrorMessage(
          res,
          res.message || 'No se pudo crear el administrador.'
        );
      }
      this.creating = false;
    },
    openEditDialog(item) {
      this.editTarget = item;
      this.editAdmin = {
        name: item.name || '',
        surname: item.surname || '',
        email: item.email || '',
        rut: item.rut ? RutUtils.formatRut(item.rut) : ''
      };
      this.editError = null;
      this.editDialog = true;
      if (this.$refs.editForm) this.$refs.editForm.resetValidation();
    },
    // Only the fields the admin actually changed travel to the backend (PATCH).
    changedFields() {
      const target = this.editTarget;
      const fields = {};
      ['name', 'surname', 'email'].forEach((key) => {
        const value = this.editAdmin[key].trim();
        if (value !== (target[key] || '')) fields[key] = value;
      });
      if (RutUtils.clean(this.editAdmin.rut) !== RutUtils.clean(target.rut)) {
        fields.rut = this.editAdmin.rut;
      }
      return fields;
    },
    async submitEdit() {
      if (!this.$refs.editForm.validate()) return;
      const fields = this.changedFields();
      if (Object.keys(fields).length === 0) {
        this.editDialog = false;
        return;
      }

      this.editing = true;
      this.editError = null;
      const res = await EjecutivosService.update(this.editTarget.id, fields);
      if (res.status === 'ok') {
        this.editDialog = false;
        this.snackbarText = 'Administrador actualizado.';
        this.snackbar = true;
        await this.loadEjecutivos();
      } else {
        this.editError = this.adminErrorMessage(res, 'No se pudo actualizar el administrador.');
      }
      this.editing = false;
    },
    askResetMfa(item) {
      this.mfaResetTarget = item;
      this.confirmMfaReset = true;
    },
    async resetMfa() {
      const target = this.mfaResetTarget;
      this.confirmMfaReset = false;
      if (!target) return;

      this.resettingId = target.id;
      const res = await MfaService.reset(target.id);
      if (res.status === 'ok') {
        this.snackbarText = `Se reseteó el segundo factor de ${target.name}.`;
        this.snackbar = true;
        // Reload so the MFA chip flips to "Pendiente" and the button disables.
        await this.loadEjecutivos();
      } else if (res.code === 'OPERATION_NOT_AUTHORIZED') {
        this.error = 'No puedes resetear tu propio segundo factor.';
      } else {
        this.error = 'No se pudo resetear el segundo factor del usuario.';
      }
      this.resettingId = null;
      this.mfaResetTarget = null;
    },
  },
};
</script>
